import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import ActionsMenu from '../components/ActionsMenu';
import {
  DSBackLink, DSBadge, DSButton, DSCard, DSIcon, DSInput, DSSheet, DSTextarea
} from '../design-system/primitives';
import { hostDisplayName } from '../design-system/patterns/HostPicker';
import './SeriesDates.css';
import UserAvatar from '../components/UserAvatar';

/**
 * Every night a series has generated, as its own screen.
 *
 * This replaced a modal containing a table, where each row carried a bare "type a name"
 * input, a naked date picker and a red Delete button sitting next to it. The work here is
 * assigning hosts and occasionally moving or calling off a night; none of that belongs in
 * a dialog, and none of it should be one mis-click from destroying a night's RSVPs.
 */

const FILTERS = [
  { key: 'all', label: 'All dates' },
  { key: 'needs-host', label: 'Needs a host' },
  { key: 'hosted', label: 'Hosted' },
  { key: 'cancelled', label: 'Cancelled' }
];

const monthLabel = (date) => date.toLocaleDateString(undefined, { month: 'short' }).toUpperCase();
const weekdayLabel = (date) => date.toLocaleDateString(undefined, { weekday: 'long' });
const timeLabel = (date) => date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

// Board game and video game nights carry a featured game; book club carries a book. The
// old manage-events table owned this, and it would have been quietly lost in the rebuild.
const featuredLabelFor = (category) => {
  if (category === 'book_club') return 'featured book';
  if (category === 'board_game' || category === 'video_game') return 'featured game';
  return null;
};

const people = (count) => `${count} ${count === 1 ? 'person' : 'people'}`;

/**
 * What a cancel or reinstate actually did beyond the app: how many people with an RSVP
 * were emailed, and whether the website took the change. Exported for tests.
 */
export const describeDateOutcome = (lead, outcome = {}) => {
  const parts = [lead];
  const notified = outcome?.notified;
  let problem = false;

  if (notified) {
    if (notified.error) {
      parts.push('Could not email the people who RSVP\'d.');
      problem = true;
    } else if (!notified.total) {
      parts.push('Nobody had RSVP\'d, so no emails went out.');
    } else {
      parts.push(`${people(notified.sent)} notified.`);
      if (notified.failed > 0) {
        parts.push(`${notified.failed} ${notified.failed === 1 ? 'email' : 'emails'} failed.`);
        problem = true;
      }
    }
  }

  // Null means the event is not on the website at all (a private event): nothing to say.
  if (outcome?.websiteUpdated === false) {
    parts.push(`The website did not update${outcome.websiteError ? ` (${outcome.websiteError})` : ''}. The nightly sync will try again.`);
    problem = true;
  }

  return { message: parts.join(' '), type: problem ? 'warning' : 'success' };
};

const showOutcomeToast = (lead, outcome) => {
  const { message, type } = describeDateOutcome(lead, outcome);
  // A warning stays up long enough to read the reason.
  showToast(message, type, type === 'warning' ? 9000 : 4000);
};

function SeriesDates() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const [series, setSeries] = useState(null);
  const [dates, setDates] = useState([]);
  const [hostCandidates, setHostCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const [assignTarget, setAssignTarget] = useState(null);
  const [assignQuery, setAssignQuery] = useState('');
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveValue, setMoveValue] = useState('');
  const [featuredDraft, setFeaturedDraft] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addValue, setAddValue] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await apiService.get(`/event-series/${id}/dates`);
      setSeries(response.data.series);
      setDates(response.data.dates || []);
      setError(null);
    } catch (err) {
      console.error('Error loading series dates:', err);
      setError('Could not load the dates for this series.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isAdmin) {
      load();
    } else {
      setLoading(false);
    }
  }, [isAdmin, load]);

  useEffect(() => {
    if (!isAdmin) return;

    apiService.get('/event-series/host-candidates')
      .then((response) => setHostCandidates(response.data || []))
      .catch((err) => console.error('Error loading host candidates:', err));
  }, [isAdmin]);

  const counters = useMemo(() => {
    const now = new Date();
    const live = dates.filter((date) => !date.cancelled_at);

    return {
      dates: live.length,
      // Only nights still ahead: a past night with no host is a fact, not a task, and
      // counting it puts a number on the screen nobody can act on.
      needsHost: live.filter((date) => (date.hosts || []).length === 0 && new Date(date.event_date) >= now).length,
      signups: live.reduce((total, date) => total + (date.signup_count || 0), 0)
    };
  }, [dates]);

  const visibleDates = useMemo(() => dates.filter((date) => {
    const hosted = (date.hosts || []).length > 0;

    if (filter === 'cancelled') return Boolean(date.cancelled_at);
    if (date.cancelled_at) return filter === 'all';
    if (filter === 'needs-host') return !hosted;
    if (filter === 'hosted') return hosted;

    return true;
  }), [dates, filter]);

  const runUpdate = async (eventId, work, successMessage) => {
    setBusyId(eventId);
    try {
      await work();
      await load();
      if (successMessage) {
        showToast(successMessage, 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'That did not work. Try again.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleAssign = (date, userId) => {
    const replacing = date.replacingHostId;
    setAssignTarget(null);
    setAssignQuery('');

    runUpdate(
      date.id,
      async () => {
        if (replacing) {
          await apiService.delete(`/events/${date.id}/remove-host/${replacing}`);
        }
        await apiService.post(`/events/${date.id}/assign-host`, { userId });
      },
      replacing ? 'Host changed. Reminders follow the new host.' : 'Host assigned. Reminders are scheduled.'
    );
  };

  // "Change" replaces the host. It used to just remove them, which left the night
  // unhosted with no indication that was what the button did.
  const handleChangeHost = (date, currentHostId) => {
    setAssignQuery('');
    setAssignTarget({ ...date, replacingHostId: currentHostId });
  };

  const handleMove = () => {
    const target = moveTarget;
    const value = moveValue;
    setMoveTarget(null);

    runUpdate(
      target.id,
      // Sent as the wall clock the admin typed, not converted to UTC. Event times are
      // stored without a timezone, so toISOString() moved an 8pm night to 1am the next
      // day for anyone east of UTC.
      () => apiService.post(`/event-series/dates/${target.id}/move`, {
        event_date: value.length === 16 ? `${value}:00` : value
      }),
      'Date moved. Anyone signed up can see the new time.'
    );
  };

  // Asks for a reason rather than just confirming, because the reason is what the public
  // event page shows. The night stays up on the website with the cancellation on it, and
  // "cancelled" on its own leaves everyone guessing whether it is coming back.
  const openCancel = (date) => {
    setCancelTarget(date);
    setCancelReason('');
  };

  const handleCancel = async () => {
    const target = cancelTarget;
    if (!target) return;

    setCancelSubmitting(true);

    try {
      const response = await apiService.post(`/event-series/dates/${target.id}/cancel`, {
        reason: cancelReason.trim() || undefined
      });
      setCancelTarget(null);
      await load();
      showOutcomeToast('Night cancelled.', response?.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not cancel that night', 'error');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleReinstate = (date) => {
    runUpdate(
      date.id,
      async () => {
        const response = await apiService.post(`/event-series/dates/${date.id}/reinstate`, {});
        // Inside the work, so it only shows once the reinstate has actually gone through.
        showOutcomeToast('Night is back on.', response?.data);
      },
      null
    );
  };

  // A one-off night on top of whatever the recurrence produced: a make-up week, a
  // finals night, a date the pattern does not cover.
  const openAddDate = () => {
    const next = new Date();
    next.setDate(next.getDate() + 7);
    const pad = (value) => String(value).padStart(2, '0');
    const time = series?.time_of_day ? series.time_of_day.slice(0, 5) : '19:00';

    setAddValue(`${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}T${time}`);
    setAddOpen(true);
  };

  const handleAddDate = () => {
    const value = addValue;
    setAddOpen(false);

    runUpdate(
      'new',
      () => apiService.post(`/event-series/${series.id}/dates`, {
        event_date: value.length === 16 ? `${value}:00` : value
      }),
      'Date added'
    );
  };

  const openMove = (date) => {
    const current = new Date(date.event_date);
    const pad = (value) => String(value).padStart(2, '0');
    setMoveValue(
      `${current.getFullYear()}-${pad(current.getMonth() + 1)}-${pad(current.getDate())}T${pad(current.getHours())}:${pad(current.getMinutes())}`
    );
    setMoveTarget(date);
  };

  const matchingCandidates = useMemo(() => {
    const query = assignQuery.trim().toLowerCase();
    const assigned = new Set((assignTarget?.hosts || []).map((host) => host.id));

    return hostCandidates
      .filter((candidate) => !assigned.has(candidate.id))
      .filter((candidate) => !query || hostDisplayName(candidate).toLowerCase().includes(query))
      .slice(0, 40);
  }, [assignQuery, assignTarget, hostCandidates]);

  if (!isAdmin) {
    return (
      <div className="series-dates-page">
        <div className="error">You must be an admin to access this page.</div>
      </div>
    );
  }

  if (loading) {
    return <div className="series-dates-page"><div className="loading">Loading dates...</div></div>;
  }

  return (
    <div className="series-dates-page">
      {/* Back to the Admin tab's Series section - where this screen is opened from -
          rather than dropping the user on Upcoming. */}
      <DSBackLink onClick={() => navigate('/events?tab=admin&section=series')}>Events</DSBackLink>

      <h1 className="series-dates__title">Series dates</h1>
      <p className="series-dates__subtitle">
        Every night generated for {series?.name || 'this series'}. Assign a host, move a date,
        or cancel a night. Members see these dates as soon as they are created.
      </p>

      {error && <div className="error">{error}</div>}

      <div className="series-dates__counters">
        <div className="series-dates__counter">
          <span className="series-dates__counter-label">Dates</span>
          <span className="series-dates__counter-value">{counters.dates}</span>
        </div>
        <div className="series-dates__counter">
          <span className="series-dates__counter-label">Need a host</span>
          <span className="series-dates__counter-value">{counters.needsHost}</span>
        </div>
        <div className="series-dates__counter">
          <span className="series-dates__counter-label">Signups</span>
          <span className="series-dates__counter-value">{counters.signups}</span>
        </div>
      </div>

      <div className="series-dates__filters" role="group" aria-label="Filter dates">
        {FILTERS.map((chip) => (
          <button
            key={chip.key}
            type="button"
            className={`series-dates__filter${filter === chip.key ? ' is-active' : ''}`}
            aria-pressed={filter === chip.key}
            onClick={() => setFilter(chip.key)}
          >
            {chip.label}
          </button>
        ))}

        <button
          type="button"
          className="series-dates__filter series-dates__filter--add"
          onClick={openAddDate}
        >
          + Add a date
        </button>
      </div>

      {counters.needsHost > 0 && (
        <div className="series-dates__banner">
          <DSIcon name="warning" size="sm" tone="warning" />
          <span>
            {counters.needsHost} {counters.needsHost === 1 ? 'date has' : 'dates have'} no host.
            Nights without a host stay visible to members but nobody is reminded to open up.
          </span>
        </div>
      )}

      {visibleDates.length === 0 ? (
        <DSCard cornerVariant="md" className="series-dates__empty">
          <p>
            {dates.length === 0
              ? 'This series has not generated any dates yet. Use Generate events on the series, or add a date here.'
              : 'No dates match this filter.'}
          </p>
        </DSCard>
      ) : (
        <div className="series-dates__list">
          {visibleDates.map((date) => {
            const when = new Date(date.event_date);
            const hosts = date.hosts || [];
            const isCancelled = Boolean(date.cancelled_at);
            const isBusy = busyId === date.id;
            const featuredLabel = featuredLabelFor(series?.event_category);

            return (
              <DSCard
                key={date.id}
                cornerVariant="md"
                className={`date-card${isCancelled ? ' is-cancelled' : ''}`}
              >
                <div className="date-card__head">
                  <div className="date-card__stamp" aria-hidden="true">
                    <span className="date-card__month">{monthLabel(when)}</span>
                    <span className="date-card__day">{when.getDate()}</span>
                  </div>

                  <div className="date-card__when">
                    <span className="date-card__weekday">
                      {weekdayLabel(when)}
                      <span className="date-card__time">{timeLabel(when)}</span>
                      {isCancelled && <DSBadge variant="error" size="sm" uppercase>Cancelled</DSBadge>}
                      {!isCancelled && date.rescheduled_from && (
                        <DSBadge variant="info" size="sm" uppercase>Moved</DSBadge>
                      )}
                    </span>
                    <span className="date-card__signups">
                      {date.signup_count === 0
                        ? 'No players signed up'
                        : `${date.signup_count} player${date.signup_count === 1 ? '' : 's'} signed up`}
                      {date.rescheduled_from && !isCancelled && (
                        <> &middot; moved from {timeLabel(new Date(date.rescheduled_from))}</>
                      )}
                    </span>
                  </div>

                  <ActionsMenu
                    compact
                    label={`More actions for ${weekdayLabel(when)} ${monthLabel(when)} ${when.getDate()}`}
                    actions={[
                      !isCancelled && featuredLabel && {
                        label: `${date.event_metadata?.game_name ? 'Change' : 'Set'} ${featuredLabel}`,
                        onClick: () => setFeaturedDraft({
                          eventId: date.id,
                          label: featuredLabel,
                          name: date.event_metadata?.game_name || '',
                          description: date.event_metadata?.game_mechanics_description || '',
                          metadata: date.event_metadata || {}
                        })
                      },
                      !isCancelled && { label: 'Move date', onClick: () => openMove(date) },
                      !isCancelled && { label: 'Cancel night', onClick: () => openCancel(date), tone: 'danger' },
                      isCancelled && { label: 'Put the night back', onClick: () => handleReinstate(date) }
                    ]}
                  />
                </div>

                {date.event_metadata?.game_name && (
                  <p className="date-card__featured">
                    <DSIcon name="gamepad" size="sm" tone="muted" /> {date.event_metadata.game_name}
                  </p>
                )}

                {hosts.length > 0 ? (
                  hosts.map((host) => (
                    <div key={host.id} className="date-card__host">
                      <UserAvatar person={host} className="date-card__avatar" />
                      <span className="date-card__host-main">
                        <span className="date-card__host-name">{host.name}</span>
                        <span className="date-card__host-detail">Host confirmed &middot; reminders on</span>
                      </span>
                      <DSButton
                        appearance="outline"
                        tone="neutral"
                        size="sm"
                        disabled={isBusy || isCancelled}
                        onClick={() => handleChangeHost(date, host.id)}
                      >
                        Change
                      </DSButton>
                    </div>
                  ))
                ) : isCancelled ? (
                  // A called-off night does not need a host, and saying it does put it in
                  // the "needs a host" count in the reader's head even though it is not.
                  <div className="date-card__host date-card__host--off">
                    <span className="date-card__host-main">
                      <span className="date-card__host-detail">
                        This night is cancelled. Put it back to assign a host.
                      </span>
                    </span>
                    <DSButton
                      appearance="outline"
                      tone="neutral"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => handleReinstate(date)}
                    >
                      Put back
                    </DSButton>
                  </div>
                ) : (
                  <div className="date-card__host date-card__host--empty">
                    <span className="date-card__avatar date-card__avatar--empty" aria-hidden="true">?</span>
                    <span className="date-card__host-main">
                      <span className="date-card__host-name">Needs a host</span>
                    </span>
                    <DSButton
                      appearance="solid"
                      tone="primary"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => { setAssignQuery(''); setAssignTarget(date); }}
                    >
                      Assign
                    </DSButton>
                  </div>
                )}
              </DSCard>
            );
          })}
        </div>
      )}

      {assignTarget && (
        <DSSheet
          title="Assign a host"
          onClose={() => setAssignTarget(null)}
          footer={(
            <DSButton appearance="outline" tone="neutral" onClick={() => setAssignTarget(null)}>
              Close
            </DSButton>
          )}
        >
          <p className="assign-sheet__intro">
            {weekdayLabel(new Date(assignTarget.event_date))}, {monthLabel(new Date(assignTarget.event_date))}{' '}
            {new Date(assignTarget.event_date).getDate()} at {timeLabel(new Date(assignTarget.event_date))}.
            The host gets reminders 7, 3 and 1 day out.
          </p>

          <DSInput
            label="Find a member"
            type="search"
            value={assignQuery}
            onChange={(e) => setAssignQuery(e.target.value)}
            placeholder="Type a name..."
            leadingIcon={<DSIcon name="search" size="sm" tone="muted" />}
          />

          <div className="assign-sheet__list">
            {matchingCandidates.length === 0 ? (
              <p className="assign-sheet__empty">No members match that.</p>
            ) : (
              matchingCandidates.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  className="assign-sheet__option"
                  onClick={() => handleAssign(assignTarget, candidate.id)}
                >
                  <UserAvatar person={candidate} className="date-card__avatar" />
                  <span className="assign-sheet__option-main">
                    <span className="assign-sheet__option-name">{hostDisplayName(candidate)}</span>
                    <span className="assign-sheet__option-detail">
                      {candidate.hosted_count > 0
                        ? `Hosted ${candidate.hosted_count} night${candidate.hosted_count === 1 ? '' : 's'}`
                        : 'Has not hosted yet'}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </DSSheet>
      )}

      {moveTarget && (
        <DSSheet
          title="Move this date"
          onClose={() => setMoveTarget(null)}
          footer={(
            <>
              <DSButton appearance="outline" tone="neutral" onClick={() => setMoveTarget(null)}>
                Cancel
              </DSButton>
              <DSButton appearance="solid" tone="primary" onClick={handleMove} disabled={!moveValue}>
                Move date
              </DSButton>
            </>
          )}
        >
          <p className="assign-sheet__intro">
            Currently {weekdayLabel(new Date(moveTarget.event_date))}{' '}
            {monthLabel(new Date(moveTarget.event_date))} {new Date(moveTarget.event_date).getDate()}
            {' '}at {timeLabel(new Date(moveTarget.event_date))}.
            {(moveTarget.signup_count || 0) > 0 && (
              <> {moveTarget.signup_count} signed up already, and they keep their place.</>
            )}
          </p>

          <DSInput
            label="New date and time"
            type="datetime-local"
            value={moveValue}
            onChange={(e) => setMoveValue(e.target.value)}
          />
        </DSSheet>
      )}

      {cancelTarget && (
        <DSSheet
          title="Cancel this night"
          onClose={() => setCancelTarget(null)}
          footer={(
            <>
              <DSButton appearance="outline" tone="neutral" onClick={() => setCancelTarget(null)}>
                Keep it
              </DSButton>
              <DSButton
                appearance="solid"
                tone="danger"
                onClick={handleCancel}
                disabled={cancelSubmitting}
              >
                {cancelSubmitting ? 'Cancelling...' : 'Cancel night'}
              </DSButton>
            </>
          )}
        >
          <p className="assign-sheet__intro">
            {(cancelTarget.signup_count || 0) > 0
              ? `${cancelTarget.signup_count} ${cancelTarget.signup_count === 1 ? 'person has' : 'people have'} signed up. They will be emailed, and will see this night marked cancelled in the app.`
              : 'Nobody is signed up for this night.'}
            {' '}It stays on the website with the cancellation on it rather than disappearing,
            and you can put it back later.
          </p>

          <DSTextarea
            label="Reason (optional)"
            rows="3"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Shown on the event page and in the email, e.g. host is unwell, venue closed for weather"
          />
        </DSSheet>
      )}

      {addOpen && (
        <DSSheet
          title="Add a date"
          onClose={() => setAddOpen(false)}
          footer={(
            <>
              <DSButton appearance="outline" tone="neutral" onClick={() => setAddOpen(false)}>
                Cancel
              </DSButton>
              <DSButton appearance="solid" tone="primary" onClick={handleAddDate} disabled={!addValue}>
                Add date
              </DSButton>
            </>
          )}
        >
          <p className="assign-sheet__intro">
            A one-off night on this series, on top of whatever the recurrence generates.
            It takes the series' location, category and length.
          </p>

          <DSInput
            label="Date and time"
            type="datetime-local"
            value={addValue}
            onChange={(e) => setAddValue(e.target.value)}
          />
        </DSSheet>
      )}

      {featuredDraft && (
        <DSSheet
          title={`${featuredDraft.name ? 'Change' : 'Set'} ${featuredDraft.label}`}
          onClose={() => setFeaturedDraft(null)}
          footer={(
            <>
              <DSButton appearance="outline" tone="neutral" onClick={() => setFeaturedDraft(null)}>
                Cancel
              </DSButton>
              <DSButton
                appearance="solid"
                tone="primary"
                disabled={!featuredDraft.name.trim()}
                onClick={() => {
                  const draft = featuredDraft;
                  setFeaturedDraft(null);
                  runUpdate(
                    draft.eventId,
                    () => apiService.put(`/events/${draft.eventId}`, {
                      event_metadata: {
                        ...draft.metadata,
                        game_name: draft.name.trim(),
                        game_mechanics_description: draft.description.trim()
                      }
                    }),
                    'Saved'
                  );
                }}
              >
                Save
              </DSButton>
            </>
          )}
        >
          <DSInput
            label={`${featuredDraft.label.charAt(0).toUpperCase()}${featuredDraft.label.slice(1)} name`}
            type="text"
            value={featuredDraft.name}
            onChange={(e) => setFeaturedDraft({ ...featuredDraft, name: e.target.value })}
            placeholder="e.g., Wingspan"
          />
          <DSInput
            label="What players should know"
            type="text"
            value={featuredDraft.description}
            onChange={(e) => setFeaturedDraft({ ...featuredDraft, description: e.target.value })}
            placeholder="Optional. Rules weight, player count, anything to bring."
          />
        </DSSheet>
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

export default SeriesDates;
